// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag } from '@calendly/feature-check-react'
import { usePageTitle } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { TitleText } from '@calendly/ui/components/typography'
import { useMemo } from 'react'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'

import { TabContainer, TabContent, PageHeaderWithTabs } from 'components/page_elements'
import { ActivityLog } from 'features/activity_log'
import { DataDeletion, Visibility, BookingVerificationSettings } from 'features/organization_settings'
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context'

import { generateTabs } from '../helpers'
import { getAdminSecurityFullPageTabs, getAdminSecurityModalTabs } from '../settings_section_tabs'

const DataDeletionTab = () => (
  <TabContainer narrow>
    <TitleText level={2} size="medium">
      {I18n.t('publisher.admin.security.tabs.data_deletion')}
    </TitleText>
    <TabContent>
      <DataDeletion />
    </TabContent>
  </TabContainer>
)

const ActivityLogTab = () => (
  <TabContainer>
    <TitleText level={2} size="medium">
      {I18n.t('publisher.admin.security.tabs.activity_log')}
    </TitleText>
    <TabContent>
      <ActivityLog />
    </TabContent>
  </TabContainer>
)

const VisibilityTab = () => (
  <TabContainer>
    <TitleText level={2} size="medium">
      {I18n.t('publisher.organization_settings.visibility.title')}
    </TitleText>
    <TabContent>
      <Visibility />
    </TabContent>
  </TabContainer>
)

const BookingTab = () => {
  const inSettingsModal = useIsSettingsModalLayout()

  return (
    <TabContainer>
      {inSettingsModal && (
        <TitleText level={2} size="medium">
          {I18n.t('publisher.admin.security.tabs.booking')}
        </TitleText>
      )}
      <TabContent>
        <Booking />
      </TabContent>
    </TabContainer>
  )
}

const Booking = () => {
  usePageTitle(`${I18n.t('publisher.admin.security.tabs.booking')} - ${I18n.t('publisher.admin.security.title')}`)

  return (
    <BookingVerificationSettings />
  )
}

export const SecurityPage = () => {
  const { pathname } = useLocation()
  const inSettingsModal = useIsSettingsModalLayout()
  const settingsModalShellOwnsTabs = inSettingsModal
  const joinOrgExperiment = useFlag('obi_home_join_an_org_experiment')
  const bookingVerificationSettingsEnabled = useFlag('trust_and_safety_email_verification_feature')

  const tabs = useMemo(() => {
    if (inSettingsModal) {
      return getAdminSecurityModalTabs(bookingVerificationSettingsEnabled)
    }
    return getAdminSecurityFullPageTabs({ bookingVerificationSettingsEnabled, joinOrgExperiment })
  }, [inSettingsModal, bookingVerificationSettingsEnabled, joinOrgExperiment])

  const routes = (
    <Switch>
      <Redirect exact from="/app/admin/security" to="/app/admin/security/data_deletion" />
      {inSettingsModal && (
        <Redirect
          exact
          from="/app/admin/security/activity_log"
          to="/app/admin/security/data_deletion"
        />
      )}
      {inSettingsModal && (
        <Redirect
          exact
          from="/app/admin/security/visibility"
          to="/app/admin/security/data_deletion"
        />
      )}
      <Route path="/app/admin/security/data_deletion" component={DataDeletionTab} />
      <Route path="/app/admin/security/activity_log" component={ActivityLogTab} />
      {joinOrgExperiment && (
        <Route path="/app/admin/security/visibility" component={VisibilityTab} />
      )}
      {bookingVerificationSettingsEnabled && (
        <Route path="/app/admin/security/booking" component={BookingTab} />
      )}
    </Switch>
  )

  if (settingsModalShellOwnsTabs) {
    return routes
  }

  return (
    <>
      <PageHeaderWithTabs
        hideTitleChrome={inSettingsModal}
        title={I18n.t('publisher.admin.security.title')}
        eyebrow={(
          <TitleText variant="eyebrow">
            {I18n.t('publisher.admin.navigation.organization')}
          </TitleText>
        )}
        tabs={generateTabs({ pathname, tabs })}
      />
      {routes}
    </>
  )
}
